/**
 * AITargetingHelper
 * AI-powered targeting suggestions component
 * Automatically uses brand data to generate interest/behavior suggestions
 * Returns validated platform IDs that ad platforms accept
 */

import React, { useState, useMemo } from 'react';
import {
    Sparkles,
    Loader2,
    Plus,
    Check,
    AlertCircle,
    RefreshCw
} from 'lucide-react';

export default function AITargetingHelper({
    platform,
    type = 'interest',
    brandId,
    userId,
    brand, // Brand object with name, industry, products, audience
    selectedItems = [],
    onSelect,
    funnelStage = 'TOFU',
    disabled = false
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [searchTerms, setSearchTerms] = useState([]);

    // Track which suggestions have been added
    const [addedIds, setAddedIds] = useState(new Set());

    // Build context from brand data
    const brandContext = useMemo(() => {
        if (!brand) return null;

        return {
            businessDescription: [
                brand.name,
                brand.industry ? `in the ${brand.industry} industry` : '',
                brand.products ? `offering ${brand.products}` : ''
            ].filter(Boolean).join(' '),
            productDescription: brand.products || '',
            targetAudience: [
                brand.audience?.demographics || '',
                brand.audience?.interests ? `Interests: ${brand.audience.interests}` : '',
                brand.audience?.painPoints ? `Pain points: ${brand.audience.painPoints}` : ''
            ].filter(Boolean).join('. ')
        };
    }, [brand]);

    const hasBrandData = brandContext &&
        (brandContext.businessDescription || brandContext.productDescription || brandContext.targetAudience);

    const generateSuggestions = async () => {
        if (!hasBrandData) {
            setError('Please complete your brand profile to use AI suggestions');
            return;
        }

        setLoading(true);
        setError(null);
        setSuggestions([]);
        setSearchTerms([]);

        try {
            const response = await fetch(`/api/targeting/${platform}/ai-suggestions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    brandId,
                    userId,
                    type,
                    businessDescription: brandContext.businessDescription,
                    productDescription: brandContext.productDescription,
                    targetAudience: brandContext.targetAudience,
                    funnelStage
                })
            });

            if (!response.ok) {
                let errorMessage = 'Failed to generate suggestions';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorMessage;
                } catch {
                    // Response wasn't JSON, use status text
                    errorMessage = `Server error: ${response.status} ${response.statusText}`;
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();
            setSuggestions(data.suggestions || []);
            setSearchTerms(data.searchTerms || []);

            if (data.suggestions?.length === 0 && data.searchTerms?.length > 0) {
                setError('AI suggested terms but no matching platform options were found. The platform connection may need to be refreshed.');
            } else if (data.suggestions?.length === 0) {
                setError('No matching targeting options found. Try updating your brand profile.');
            }
        } catch (err) {
            console.error('AI suggestions error:', err);
            // Handle specific error cases
            if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
                setError('Cannot connect to server. Please ensure the server is running.');
            } else if (err.message.includes('requiresConnection')) {
                setError(`Please connect your ${platform} account first.`);
            } else if (err.message.includes('429') || err.message.includes('rate limit') || err.message.includes('Rate limit')) {
                setError('AI service is busy. Please wait a moment and try again.');
            } else {
                setError(err.message || 'Failed to generate suggestions');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAddSuggestion = (suggestion) => {
        if (selectedItems.some(item => item.id === suggestion.id)) {
            return;
        }

        onSelect({
            id: suggestion.id,
            name: suggestion.name,
            type: suggestion.type || type,
            path: suggestion.path,
            audienceSize: suggestion.audienceSize
        });

        setAddedIds(prev => new Set([...prev, suggestion.id]));
    };

    const handleAddAll = () => {
        const newItems = suggestions
            .filter(s => !selectedItems.some(item => item.id === s.id) && !addedIds.has(s.id))
            .map(suggestion => ({
                id: suggestion.id,
                name: suggestion.name,
                type: suggestion.type || type,
                path: suggestion.path,
                audienceSize: suggestion.audienceSize
            }));

        if (newItems.length > 0) {
            // Pass all items at once - onSelect should handle arrays
            onSelect(newItems);
            setAddedIds(prev => new Set([...prev, ...newItems.map(s => s.id)]));
        }
    };

    const isAdded = (suggestion) => {
        return selectedItems.some(item => item.id === suggestion.id) || addedIds.has(suggestion.id);
    };

    const formatAudienceSize = (size) => {
        if (!size) return null;
        if (size >= 1000000000) return `${(size / 1000000000).toFixed(1)}B`;
        if (size >= 1000000) return `${(size / 1000000).toFixed(1)}M`;
        if (size >= 1000) return `${(size / 1000).toFixed(0)}K`;
        return size.toString();
    };

    const typeLabel = {
        interest: 'Interests',
        behavior: 'Behaviors',
        affinity: 'Affinity Audiences',
        inMarket: 'In-Market Audiences',
        skill: 'Skills',
        title: 'Job Titles'
    }[type] || type;

    const availableSuggestions = suggestions.filter(s => !isAdded(s));

    return (
        <div className="ai-targeting-helper">
            {/* Generate Button */}
            <button
                type="button"
                className="ai-generate-btn"
                onClick={generateSuggestions}
                disabled={disabled || loading || !hasBrandData}
                title={!hasBrandData ? 'Complete your brand profile to use AI suggestions' : `Generate ${typeLabel} suggestions based on your brand`}
            >
                {loading ? (
                    <>
                        <Loader2 size={14} className="spinning" />
                        <span>Finding {typeLabel}...</span>
                    </>
                ) : suggestions.length > 0 ? (
                    <>
                        <RefreshCw size={14} />
                        <span>Regenerate AI {typeLabel}</span>
                    </>
                ) : (
                    <>
                        <Sparkles size={14} />
                        <span>AI Suggest {typeLabel}</span>
                    </>
                )}
            </button>

            {/* Error Message */}
            {error && (
                <div className="ai-error">
                    <AlertCircle size={14} />
                    <span>{error}</span>
                </div>
            )}

            {/* Search Terms Used */}
            {searchTerms.length > 0 && (
                <div className="search-terms">
                    <span className="terms-label">AI searched:</span>
                    <div className="terms-list">
                        {searchTerms.slice(0, 8).map((term, idx) => (
                            <span key={idx} className="term-tag">{term}</span>
                        ))}
                        {searchTerms.length > 8 && (
                            <span className="term-tag more">+{searchTerms.length - 8} more</span>
                        )}
                    </div>
                </div>
            )}

            {/* Suggestions List */}
            {suggestions.length > 0 && (
                <div className="suggestions-section">
                    <div className="suggestions-header">
                        <span>{suggestions.length} AI suggestions</span>
                        {availableSuggestions.length > 0 && (
                            <button
                                type="button"
                                className="add-all-btn"
                                onClick={handleAddAll}
                            >
                                <Plus size={14} />
                                Add All ({availableSuggestions.length})
                            </button>
                        )}
                    </div>

                    <div className="suggestions-list">
                        {suggestions.map((suggestion) => (
                            <div
                                key={suggestion.id}
                                className={`suggestion-item ${isAdded(suggestion) ? 'added' : ''}`}
                            >
                                <div className="suggestion-content">
                                    <span className="suggestion-name">{suggestion.name}</span>
                                    {suggestion.path && (
                                        <span className="suggestion-path">{suggestion.path}</span>
                                    )}
                                </div>
                                <div className="suggestion-meta">
                                    {suggestion.audienceSize && (
                                        <span className="suggestion-size">
                                            {formatAudienceSize(suggestion.audienceSize)}
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        className={`add-btn ${isAdded(suggestion) ? 'added' : ''}`}
                                        onClick={() => handleAddSuggestion(suggestion)}
                                        disabled={isAdded(suggestion)}
                                    >
                                        {isAdded(suggestion) ? (
                                            <Check size={14} />
                                        ) : (
                                            <Plus size={14} />
                                        )}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <style>{`
                .ai-targeting-helper {
                    margin-bottom: 0.75rem;
                }

                .ai-generate-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    width: 100%;
                    padding: 0.625rem 0.75rem;
                    background: linear-gradient(135deg, var(--color-primary-light, #e8f4fd) 0%, var(--color-surface) 100%);
                    border: 1px dashed var(--color-primary);
                    border-radius: 6px;
                    font-size: 0.8125rem;
                    font-weight: 500;
                    color: var(--color-primary);
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .ai-generate-btn:hover:not(:disabled) {
                    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark, #1a73c7) 100%);
                    color: white;
                    border-style: solid;
                }

                .ai-generate-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .spinning {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .ai-error {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-top: 0.5rem;
                    padding: 0.5rem 0.625rem;
                    background: var(--color-error-light, #fef2f2);
                    border: 1px solid var(--color-error);
                    border-radius: 4px;
                    font-size: 0.75rem;
                    color: var(--color-error);
                }

                .search-terms {
                    margin-top: 0.5rem;
                    padding: 0.5rem;
                    background: var(--color-bg-secondary);
                    border-radius: 4px;
                }

                .terms-label {
                    font-size: 0.6875rem;
                    color: var(--color-text-tertiary);
                    display: block;
                    margin-bottom: 0.375rem;
                }

                .terms-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.25rem;
                }

                .term-tag {
                    padding: 0.125rem 0.375rem;
                    background: var(--color-bg-tertiary);
                    border-radius: 3px;
                    font-size: 0.625rem;
                    color: var(--color-text-secondary);
                }

                .term-tag.more {
                    font-style: italic;
                    color: var(--color-text-tertiary);
                }

                .suggestions-section {
                    margin-top: 0.5rem;
                    padding: 0.5rem;
                    background: var(--color-surface);
                    border: 1px solid var(--color-border);
                    border-radius: 6px;
                }

                .suggestions-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 0.5rem;
                    font-size: 0.75rem;
                    color: var(--color-text-secondary);
                }

                .add-all-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    padding: 0.25rem 0.5rem;
                    background: var(--color-success);
                    border: none;
                    border-radius: 4px;
                    font-size: 0.6875rem;
                    font-weight: 500;
                    color: white;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .add-all-btn:hover {
                    background: var(--color-success-dark, #059669);
                }

                .suggestions-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                    max-height: 200px;
                    overflow-y: auto;
                }

                .suggestion-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.375rem 0.5rem;
                    background: var(--color-bg-primary);
                    border-radius: 4px;
                    transition: background 0.15s;
                }

                .suggestion-item:hover:not(.added) {
                    background: var(--color-bg-secondary);
                }

                .suggestion-item.added {
                    opacity: 0.5;
                }

                .suggestion-content {
                    flex: 1;
                    min-width: 0;
                }

                .suggestion-name {
                    display: block;
                    font-size: 0.75rem;
                    color: var(--color-text-primary);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .suggestion-path {
                    display: block;
                    font-size: 0.625rem;
                    color: var(--color-text-tertiary);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .suggestion-meta {
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                    flex-shrink: 0;
                    margin-left: 0.5rem;
                }

                .suggestion-size {
                    font-size: 0.625rem;
                    color: var(--color-text-tertiary);
                }

                .add-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 20px;
                    height: 20px;
                    background: var(--color-primary);
                    border: none;
                    border-radius: 4px;
                    color: white;
                    cursor: pointer;
                    transition: all 0.15s;
                }

                .add-btn:hover:not(:disabled) {
                    background: var(--color-primary-dark, #1a73c7);
                }

                .add-btn.added {
                    background: var(--color-success);
                    cursor: default;
                }

                .add-btn:disabled {
                    cursor: default;
                }
            `}</style>
        </div>
    );
}
